import Graphic from '@arcgis/core/Graphic';
import Sketch from '@arcgis/core/widgets/Sketch';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { SketchType, convertToGeoJSON, updateGraphicColor } from './graphics-handler';
import {
  DISABLED, ENABLED, ToolBarButtons, setGraphicToolState,
} from './toolbar-handler';
import Providers from './providers';
import { OnGeometryChangeHandler } from '.';
import LocalStoragePreferences from '../utils/local-storage-preferences';

export type SketchState = 'start' | 'active' | 'complete' | 'cancel'
interface SketchCreateEvent {
  state: SketchState,
  tool: SketchType,
  graphic: Graphic,
}

const DISCARD_GEOMETRY_CONFIRM_MESSAGE = 'Are you sure you want to discard current geometry?';

export const onCreate = async (
  event: SketchCreateEvent, onChangeFn: OnGeometryChangeHandler, sketch: Sketch, fetchCurrentColor: () => string,
) => {
  const { state, graphic } = event;

  if (state === 'start') {
    const { createGraphic } = sketch;
    const color = fetchCurrentColor();
    updateGraphicColor(createGraphic, color);
  }

  if (state === 'complete') {
    const { geometry } = graphic;
    const geoJson = convertToGeoJSON(geometry);

    sketch.update(graphic);
    onChangeFn(geoJson);
    await setGraphicToolState(ToolBarButtons, DISABLED);
  }
};

export const onUpdate = (
  event: any, onChangeFn: OnGeometryChangeHandler,
) => {
  const allowedUpdatingEvents = ['reshape-stop', 'scale-stop', 'move-stop', 'rotate-stop', 'vertex-remove'];
  const { toolEventInfo, graphics } = event;
  const isValidUpdatingEvent = allowedUpdatingEvents.includes(toolEventInfo?.type || '');

  if (isValidUpdatingEvent && graphics?.length) {
    const { geometry } = graphics[0];
    const geoJson = convertToGeoJSON(geometry);

    onChangeFn(geoJson);
  }
};

export const onDelete = (onChangeFn: OnGeometryChangeHandler) => {
  // eslint-disable-next-line no-alert
  onChangeFn(null);
};

export const onKeyDown = (
  event: KeyboardEvent,
  sketch: Sketch,
) => {
  const { key } = event;
  if (key !== 'Escape' || !sketch.createGraphic) {
    return;
  }

  // eslint-disable-next-line no-alert
  if (!window.confirm(DISCARD_GEOMETRY_CONFIRM_MESSAGE)) {
    event.stopPropagation();
  }
};

export const watchReady = (
  state: string,
  view?: MapView,
) => {
  if (!view) {
    return;
  }

  const existingLayer = Providers(view).sketchLayer() as GraphicsLayer;
  const graphicsCount = existingLayer ? existingLayer.graphics?.length : 0;

  if (state === 'ready' && graphicsCount === 0) {
    setGraphicToolState(ToolBarButtons, ENABLED);
  }
};

export const watchActiveTool = (tool: string, localStorage?: LocalStoragePreferences) => {
  if (!['point', 'polyline', 'polygon'].includes(tool)) {
    return;
  }

  const localStoragePreferences = localStorage ?? new LocalStoragePreferences();
  localStoragePreferences.setSketchCreateTool(tool as SketchType);
};

export const setupListeners = (
  sketch: Sketch, onChange: OnGeometryChangeHandler, currentView: MapView, fetchCurrentColor: () => string,
) => {
  currentView.on('key-down', (event) => {
    onKeyDown(event as unknown as KeyboardEvent, sketch);
  });
  sketch.on('create', (event) => {
    onCreate(event, onChange, sketch, fetchCurrentColor);
  });
  sketch.on('update', (event) => {
    onUpdate(event, onChange);
  });
  sketch.on('delete', () => {
    onDelete(onChange);
  });
  sketch.watch('state', (state) => {
    watchReady(state, currentView);
  });
  sketch.watch('activeTool', (tool) => {
    watchActiveTool(tool);
  });
};
